import type { ComponentProps, ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";

import { cn } from "@/common/utils";
import { CLI_SIGNUP_URL } from "@/landing/lib/constants";
import { useSiteCopy } from "@/landing/lib/site-copy";

const ACCENT = "#B308A7";

const PRIMARY =
  "inline-flex h-11 flex-none cursor-pointer items-center justify-center gap-2 rounded-full border-0 bg-(--g-ink) px-[26px] text-[15px] font-medium text-(--g-lit) no-underline transition-colors hover:bg-black";

/** The one primary action: an ink pill reading "Join" that goes to sign-up. */
export function JoinButton({
  className,
  children = "Join",
}: {
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Link to={CLI_SIGNUP_URL} className={cn(PRIMARY, className)}>
      {children}
    </Link>
  );
}

/**
 * The ink pill for a page whose one primary action isn't Join, such as a
 * download. A plain anchor, so it can point at a file or another origin.
 */
export function PrimaryLink({ className, ...props }: ComponentProps<"a">) {
  return <a className={cn(PRIMARY, className)} {...props} />;
}

/** Secondary action: plain ink text with a hairline underline. */
export function TextLink({ className, ...props }: ComponentProps<typeof Link>) {
  return (
    <Link
      className={cn(
        "text-[15px] text-(--g-ink) underline decoration-(--g-rule) decoration-1 underline-offset-[0.22em] transition-colors hover:decoration-current",
        className,
      )}
      {...props}
    />
  );
}

/** The hero's email field with Join inside it. Submitting goes to sign-up. */
export function EmailJoinForm({
  className,
  placeholder,
}: {
  className?: string;
  /** Defaults to the page locale's example address (site-copy.ts). */
  placeholder?: string;
}) {
  const navigate = useNavigate();
  const copy = useSiteCopy();
  return (
    <form
      className={cn(
        "g-glass-raised flex w-full max-w-[440px] items-center gap-2 rounded-full py-[10px] pr-[10px] pl-[22px]",
        className,
      )}
      onSubmit={(event) => {
        event.preventDefault();
        // The typed email rides to Tank as login_hint so its form opens prefilled.
        const email = new FormData(event.currentTarget).get("email");
        void navigate({
          to: CLI_SIGNUP_URL,
          search:
            typeof email === "string" && email.trim()
              ? { email: email.trim() }
              : {},
        });
      }}
    >
      <input
        type="email"
        name="email"
        placeholder={placeholder ?? copy.placeholder}
        aria-label={copy.emailLabel}
        className="min-w-0 flex-1 border-0 bg-transparent text-base text-(--g-ink) outline-none placeholder:text-(--g-ink-3)"
      />
      <button type="submit" className={PRIMARY}>
        {copy.join}
      </button>
    </form>
  );
}

/**
 * "Undetectable, designed to keep your accounts safe and secure." with the
 * shield. Localized pages pass the translated line as children.
 */
export function TrustLine({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  const copy = useSiteCopy();
  return (
    <p
      className={cn(
        "m-0 flex items-start gap-[7px] text-[13px] leading-[1.45] text-pretty text-(--g-ink-3)",
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden
        className="mt-[2px] size-3.5 flex-none opacity-70"
        style={{ color: ACCENT }}
      >
        <path
          fill="currentColor"
          d="M12 1.5 3.5 4.8v6.1c0 5.3 3.6 10 8.5 11.6 4.9-1.6 8.5-6.3 8.5-11.6V4.8Zm-1.3 14.9-4-4 1.6-1.6 2.4 2.4 5.4-5.4 1.6 1.6Z"
        />
      </svg>
      <span className="[&_strong]:font-medium [&_strong]:text-(--g-ink-2)">
        {children ?? (
          <>
            <strong>{copy.trustStrong}</strong>
            {copy.trustRest}
          </>
        )}
      </span>
    </p>
  );
}

/** The "AI Agent tool of 2026" award pill, in the page's language. */
export function AwardBadge({ className }: { className?: string }) {
  const copy = useSiteCopy();
  return (
    <p
      className={cn(
        "g-glass m-0 inline-flex items-center gap-2.5 rounded-full py-1.5 pr-4 pl-2.5 font-mono text-[12.5px] leading-none font-medium tracking-[0.01em] text-(--g-ink)",
        className,
      )}
    >
      <img
        src="/cesar-award.webp"
        alt="César award"
        className="h-[22px] w-auto opacity-75 [filter:brightness(0)]"
      />
      {copy.award}
    </p>
  );
}
